import type { CashSessionRow } from '../../cash/infra-api';
import { todayIsoDate } from '../infra/expenseListSql';
import { formatExpenseDate } from '../domain/expenseLabels';
import type { ExpenseCreatePaymentIntent, ExpenseSettleMethod } from './expenseSettlementMethods';

/** Días sin coincidir con hoy antes de pedir confirmación extra en efectivo de caja. */
export const PAST_EXPENSE_CASH_DRAWER_CONFIRM_DAYS = 7;

export type ExpensePaymentStatusChoice = 'unpaid' | 'paid';

export type ExpensePaidFormChoice =
  | 'cash_drawer_now'
  | 'transfer'
  | 'card'
  | 'check'
  | 'already_paid';

export type ExpenseAlreadyPaidKind = 'cash_late' | 'non_drawer';

export type ActiveCashSessionInfo = {
  session: CashSessionRow;
  operatorLabel: string | null;
};

export function expenseDateIsToday(expenseDate: string, today = todayIsoDate()): boolean {
  return expenseDate.trim().slice(0, 10) === today.slice(0, 10);
}

export function expenseDateAgeDays(expenseDate: string, today = todayIsoDate()): number {
  const expense = new Date(`${expenseDate.slice(0, 10)}T12:00:00`);
  const ref = new Date(`${today.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(expense.getTime()) || Number.isNaN(ref.getTime())) return 0;
  return Math.floor((ref.getTime() - expense.getTime()) / 86400000);
}

export function needsPastExpenseCashConfirm(expenseDate: string, today = todayIsoDate()): boolean {
  if (expenseDateIsToday(expenseDate, today)) return false;
  return expenseDateAgeDays(expenseDate, today) > PAST_EXPENSE_CASH_DRAWER_CONFIRM_DAYS;
}

export function buildLateCashPaymentNoteTemplate(expenseDate: string): string {
  const label = formatExpenseDate(expenseDate);
  return `Pagado en efectivo de la caja el ${label}, no registrado a tiempo.`;
}

export function resolveSettleMethodFromPaymentForm(input: {
  paidForm: ExpensePaidFormChoice;
  alreadyPaidKind?: ExpenseAlreadyPaidKind | null;
  nonDrawerMethod?: ExpenseSettleMethod;
}): ExpenseSettleMethod {
  if (input.paidForm === 'cash_drawer_now') return 'cash_drawer';
  if (input.paidForm === 'transfer') return 'transfer';
  if (input.paidForm === 'card') return 'card';
  if (input.paidForm === 'check') return 'check';
  if (input.paidForm === 'already_paid') {
    if (input.alreadyPaidKind === 'cash_late') return 'other';
    return input.nonDrawerMethod ?? 'other';
  }
  return 'other';
}

export function isLateCashPaymentNoteRequired(input: {
  paymentStatus: ExpensePaymentStatusChoice;
  paidForm: ExpensePaidFormChoice | null;
  alreadyPaidKind: ExpenseAlreadyPaidKind | null;
}): boolean {
  return (
    input.paymentStatus === 'paid' &&
    input.paidForm === 'already_paid' &&
    input.alreadyPaidKind === 'cash_late'
  );
}

export function canSubmitExpensePayment(input: {
  paymentStatus: ExpensePaymentStatusChoice;
  paidForm: ExpensePaidFormChoice | null;
  alreadyPaidKind: ExpenseAlreadyPaidKind | null;
  settleMethod: ExpenseSettleMethod;
  activeCashSessionId: string | null;
  settleNote: string;
  pastCashDrawerConfirmed: boolean;
  expenseDate: string;
  amount?: number;
  residual?: number;
  /** Endoso Fase C: requerido cuando settleMethod === 'check'. */
  checkId?: string | null;
}): boolean {
  if (input.paymentStatus === 'unpaid') return true;
  if (!input.paidForm) return false;
  // Endoso: el monto sale del cheque elegido — solo se valida la selección.
  if (input.settleMethod === 'check') return !!input.checkId;
  if (input.paidForm === 'already_paid' && !input.alreadyPaidKind) return false;
  if (input.settleMethod === 'cash_drawer' && !input.activeCashSessionId) return false;
  if (isLateCashPaymentNoteRequired(input) && !input.settleNote.trim()) return false;
  if (input.settleMethod === 'other' && !input.settleNote.trim()) return false;
  if (
    input.paidForm === 'cash_drawer_now' &&
    needsPastExpenseCashConfirm(input.expenseDate) &&
    !input.pastCashDrawerConfirmed
  ) {
    return false;
  }
  if (input.amount != null) {
    if (!Number.isFinite(input.amount) || input.amount <= 0) return false;
    // #752: no permitir overpay — sin epsilon de tolerancia
    if (input.residual != null && input.amount > input.residual) return false;
  }
  return true;
}

export function isoDateToPaidAt(isoDate: string): string {
  const normalized = isoDate.trim().slice(0, 10);
  return `${normalized}T12:00:00.000Z`;
}

export function defaultPaidDateForForm(expenseDate: string): string {
  return expenseDateIsToday(expenseDate) ? todayIsoDate() : expenseDate.slice(0, 10);
}
